import { useState, useEffect } from "react"
import styled from "styled-components"
import { toast } from "react-toastify"
import { useMarkSubtaskAsCompleted } from "../../Controllers/TaskManagementController"
import BaseModal from "./BaseModal"

const TaskSubtasksModal = ({ subtasks = [], onClose, open, taskId, onSubtaskComplete }) => {

    const [subtaskList, setSubtaskList] = useState(subtasks)
    const [completingTasks, setCompletingTasks] = useState(new Set())

    const { mutate: markSubtaskAsCompleted } = useMarkSubtaskAsCompleted()

    // Update subtaskList when subtasks prop changes
    useEffect(() => {
        setSubtaskList(subtasks)
    }, [subtasks])

    if (!open) return null

    if (!taskId) {
        toast.error("Task ID is missing. Cannot mark subtask as completed.")
        return null
    }

    const handleMarkSubtaskAsCompleted = (subtaskId) => {
        setCompletingTasks((prev) => new Set([...prev, subtaskId]))

        markSubtaskAsCompleted(
            { taskId, subtaskId },
            {
                onSuccess: () => {
                    toast.success("Subtask marked as completed")
                    setSubtaskList((prev) => prev.filter((s) => s.id !== subtaskId))
                    if (onSubtaskComplete) {
                        onSubtaskComplete(subtaskId)
                    }
                    setCompletingTasks((prev) => {
                        const newSet = new Set(prev)
                        newSet.delete(subtaskId)
                        return newSet
                    })
                },
                onError: () => {
                    toast.error("Failed to mark subtask as completed")
                    setCompletingTasks((prev) => {
                        const newSet = new Set(prev)
                        newSet.delete(subtaskId)
                        return newSet
                    })
                },
            },
        )
    }

    return (
        <BaseModal open={open} onClose={onClose} title="Pending Subtasks">
            <SubtasksContainer>
                {subtaskList.map((subtask) => {
                    const isCompleting = completingTasks.has(subtask.id)
                    return (
                        <SubtaskCard key={subtask.id}>
                            <SubtaskContent>
                                <SubtaskTitle>{subtask.title}</SubtaskTitle>
                                <CheckboxContainer
                                    onClick={() => handleMarkSubtaskAsCompleted(subtask.id)}
                                    $isCompleting={isCompleting}
                                >
                                    {isCompleting ? (
                                        <CompletedCheckbox>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                <path
                                                    d="M20 6L9 17L4 12"
                                                    stroke="white"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </CompletedCheckbox>
                                    ) : (
                                        <EmptyCheckbox>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <path
                                                    d="M20 6L9 17L4 12"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </EmptyCheckbox>
                                    )}
                                </CheckboxContainer>
                            </SubtaskContent>
                        </SubtaskCard>
                    )
                })}

                {subtaskList.length === 0 && (
                    <EmptyState>
                        <EmptyIcon>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                    stroke="#008cff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </EmptyIcon>
                        <EmptyText>All subtasks completed!</EmptyText>
                        <EmptySubtext>Great job on finishing all your tasks.</EmptySubtext>
                    </EmptyState>
                )}
            </SubtasksContainer>
        </BaseModal >
    )
}

const SubtasksContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`

const SubtaskCard = styled.div`
    // background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    // border-radius: 12px;
    padding: 1rem 1.25rem;
    transition: all 0.2s ease;
`

const SubtaskContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
`

const SubtaskTitle = styled.span`
    color: #e2e8f0;
    font-size: 0.95rem;
    font-weight: 400;
    line-height: 1.5;
    flex: 1;
    text-align: left;
`

const CheckboxContainer = styled.div`
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    border-radius: 6px;
    
    &:hover {
        background: rgba(255, 255, 255, 0.1);
        transform: scale(1.1);
    }
    
    &:active {
        transform: scale(0.95);
    }
`

const EmptyCheckbox = styled.div`
    width: 24px;
    height: 24px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    transition: all 0.2s ease;
    
    ${CheckboxContainer}:hover & {
        border-color: rgba(255, 255, 255, 0.7);
        color: rgba(255, 255, 255, 0.3);
    }
`

const CompletedCheckbox = styled.div`
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #008cff 0%, #0070cc 100%);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: checkboxComplete 0.3s ease;
    
    @keyframes checkboxComplete {
        0% {
            transform: scale(0.8);
            opacity: 0.8;
        }
        50% {
            transform: scale(1.1);
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }
`

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    text-align: center;
`

const EmptyIcon = styled.div`
    color: #10b981;
    margin-bottom: 1rem;
    opacity: 0.8;
`

const EmptyText = styled.p`
    color: #ffffff;
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
`

const EmptySubtext = styled.p`
    color: #94a3b8;
    font-size: 0.875rem;
    margin: 0;
`

export default TaskSubtasksModal
